// src/hooks/useMouseActivity.js
import { useEffect, useRef, useMemo } from "react";
import { MouseActivityService } from "../Services/mouseActivity.service";

/* ─────────────── CONFIG ─────────────────────────────────── */
const BUCKET_MINUTES = 1;    // each accepted mouse‐move = 1 minute
const SEND_WINDOW_MIN = 30;   // flush after 4 unsent minutes
const LS_PREFIX = "mouseActivity";
const makeKey   = cid => `${LS_PREFIX}:${cid ?? "global"}`;
const GLOBAL_KEY = makeKey(null);
/* ─────────────────────────────────────────────────────────── */

const load = key => {
  try { return JSON.parse(localStorage.getItem(key)); }
  catch { return null; }
};
const save = (key, val) => localStorage.setItem(key, JSON.stringify(val));

/* ───── flush the single “active” bucket ───── */
const flushBucket = (key, caseId, token) => {
  const st = load(key);
  if (!st || st.minutes === 0) return;

  const mins = st.minutes;
  st.minutes    = 0;
  st.lastApiUtc = new Date().toISOString();
  save(key, st);              // optimistic reset

  MouseActivityService.increment({
    caseId   : caseId ?? null,      // null if global
    bucketUtc: st.lastApiUtc,
    minutes  : mins,
    token,
  }).catch(err => {
    console.error("MouseActivity increment failed; will retry", err);
    const rollback = load(key) || st;
    rollback.minutes += mins;
    save(key, rollback);
  });
};

/* ───── the hook ─────────────────────────────────────── */
export function useMouseActivity({ caseId, token }) {
  const lastBucket = useRef(0);
  const key        = useMemo(() => makeKey(caseId), [caseId]);

  const addMinute = (key, today) => {
    let st = load(key);
    if (!st || st.date !== today) {
      // new day: reset
      st = { date: today, minutes: 0, lastApiUtc: null };
    }
    st.minutes += BUCKET_MINUTES;
    save(key, st);
    return st.minutes;
  };

  useEffect(() => {
    if (!token) return;

    const handler = () => {
      
      const now = Date.now();

      
      if (now - lastBucket.current < BUCKET_MINUTES * 60_000) return;
      lastBucket.current = now;

      const today = new Date(now).toISOString().slice(0, 10);
      const total = addMinute(key, today);

      // only flush this one bucket (no global)
      const st    = load(key);
      const last  = st.lastApiUtc ? Date.parse(st.lastApiUtc) : 0;
      
      const flush = total >= SEND_WINDOW_MIN || now - last >= SEND_WINDOW_MIN * 60_000;

      if (flush) {
        // send only once—either this case or “global”
        const sendCaseId = key === GLOBAL_KEY ? null : caseId;
        flushBucket(key, sendCaseId, token);
      }
    };

    window.addEventListener("mousemove", handler, { passive: true });
    return () => {
      const sendCaseId = key === GLOBAL_KEY ? null : caseId;
      flushBucket(key, sendCaseId, token);
      window.removeEventListener("mousemove", handler);
    };
  }, [caseId, token, key]);
}
